import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FiAlertTriangle, FiArrowRight, FiClock, FiMaximize, FiMinimize, FiPackage, FiRefreshCw, FiWifi, FiWifiOff } from 'react-icons/fi'

import { api } from '../../api/api'
import { NavBar } from '../../components/NavBar'
import { GlobalContext } from '../../context/GlobalContext'
import { SocketContext } from '../../context/SocketContext'
import { useCompany } from '../../hooks/useCompany'
import { Toast } from '../../utils/Toast'
import {
  KDS_COLUMNS,
  canonicalStatus,
  formatElapsed,
  getItemId,
  getKdsStatus,
  getSector,
  groupByOrder,
  itemCreatedAt,
  originLabel,
  statusValueFor,
  unwrapItems,
} from './kdsUtils'
import './style.css'

const POLLING_INTERVAL = 15000

function eventItem(payload) {
  return payload?.item || payload?.data || payload
}

function productName(item) {
  return item?.productName || item?.name || item?.product?.name || 'Produto'
}

function itemNote(item) {
  return item?.observation || item?.note || item?.notes || item?.obs
}

function additionals(item) {
  let values = item?.additinal || item?.additionals || item?.additional || item?.productAdditionals || []
  if (typeof values === 'string') {
    try {
      values = JSON.parse(values)
    } catch (error) {
      return [values]
    }
  }
  if (!Array.isArray(values) && values && typeof values === 'object') values = Object.values(values).flat()
  if (!Array.isArray(values)) return []
  return values.map(value => {
    if (typeof value === 'string') return value
    const quantity = Number(value?.quantity || value?.amount || 1)
    return `${quantity > 1 ? `${quantity}x ` : ''}${value?.name || value?.additional?.name || value?.product?.name || 'Adicional'}`
  })
}

function KdsItem({ item, showSector, canUpdate, updating, onMove }) {
  const sector = getSector(item)
  const statusIndex = KDS_COLUMNS.findIndex(column => column.key === canonicalStatus(getKdsStatus(item)))
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(itemCreatedAt(item)).getTime()) / 60000))
  const timeClass = elapsedMinutes >= 20 ? 'late' : elapsedMinutes >= 10 ? 'attention' : ''
  const extras = additionals(item)

  return (
    <div className="kds-item">
      <div className="kds-item-main">
        <strong className="kds-quantity">{item?.quantity || item?.amount || 1}x</strong>
        <div>
          <h3>{productName(item)}</h3>
          {extras.length > 0 && <ul>{extras.map((extra, index) => <li key={`${extra}-${index}`}>+ {extra}</li>)}</ul>}
        </div>
      </div>
      {itemNote(item) && <p className="kds-note"><strong>Obs:</strong> {itemNote(item)}</p>}
      <div className="kds-item-meta">
        <span className={`kds-time ${timeClass}`}><FiClock /> {formatElapsed(itemCreatedAt(item))}</span>
        {showSector && <span className="kds-sector">{sector.name}</span>}
      </div>
      {canUpdate && statusIndex >= 0 && statusIndex < KDS_COLUMNS.length - 1 && (
        <div className="kds-actions">
          <button type="button" className="advance" disabled={updating} onClick={() => onMove(item, statusIndex + 1)}>
            {updating ? <span className="spinner-border spinner-border-sm" /> : <>{['Aceitar', 'Iniciar preparo', 'Marcar pronto'][statusIndex]} <FiArrowRight /></>}
          </button>
        </div>
      )}
    </div>
  )
}

function OrderGroup({ group, showSector, canUpdate, updatingIds, onMove }) {
  const firstItem = group.items[0]
  return (
    <article className="kds-order-card">
      <header>
        <div><small>PEDIDO</small><strong>#{group.number}</strong></div>
        <span>{originLabel(firstItem)}</span>
      </header>
      <div className="kds-order-items">
        {group.items.map(item => <KdsItem key={getItemId(item)} item={item} showSector={showSector} canUpdate={canUpdate} updating={updatingIds.has(getItemId(item))} onMove={onMove} />)}
      </div>
    </article>
  )
}

export function KdsPage() {
  const { isAccess } = useContext(GlobalContext)
  const { connected, socket } = useContext(SocketContext)
  const { sectors, configurations } = useCompany()
  const [items, setItems] = useState([])
  const [sectorFilter, setSectorFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [updatingIds, setUpdatingIds] = useState(new Set())
  const [now, setNow] = useState(new Date())
  const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement))
  const requestRef = useRef(0)
  const canView = [true, 'admin'].includes(isAccess('kds', 'view'))
  const canUpdate = [true, 'admin'].includes(isAccess('kds', 'update_status'))

  const loadItems = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++requestRef.current
    if (silent) setRefreshing(true)
    else setLoading(true)
    try {
      const params = sectorFilter === 'none'
        ? { filter: 'sem-setor' }
        : sectorFilter === 'all'
          ? { filter: 'geral' }
          : { filter: 'setor', idSector: sectorFilter }
      const response = await api.get('/kds/items', { params })
      if (requestId === requestRef.current) {
        setItems(unwrapItems(response.data))
        setError(null)
      }
    } catch (requestError) {
      if (requestId === requestRef.current) {
        setError(requestError?.response?.data?.message || 'Não foi possível carregar a produção.')
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false)
        setRefreshing(false)
      }
    }
  }, [sectorFilter])

  useEffect(() => {
    loadItems()
    const interval = setInterval(() => loadItems({ silent: true }), POLLING_INTERVAL)
    return () => clearInterval(interval)
  }, [loadItems])

  useEffect(() => {
    const clock = setInterval(() => setNow(new Date()), 1000)
    const handleFullscreen = () => setFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', handleFullscreen)
    return () => {
      clearInterval(clock)
      document.removeEventListener('fullscreenchange', handleFullscreen)
    }
  }, [])

  useEffect(() => {
    if (!socket) return undefined

    const upsert = payload => {
      const nextItem = eventItem(payload)
      const id = getItemId(nextItem)
      if (!id) return loadItems({ silent: true })
      if (canonicalStatus(getKdsStatus(nextItem)) === 'cancelado') {
        setItems(current => current.filter(item => getItemId(item) !== id))
        return
      }
      setItems(current => {
        const exists = current.some(item => getItemId(item) === id)
        return exists ? current.map(item => getItemId(item) === id ? { ...item, ...nextItem } : item) : [nextItem, ...current]
      })
    }
    const created = payload => {
      if (configurations?.sound) document.getElementById('audio-new-order')?.play().catch(() => {})
      loadItems({ silent: true })
    }
    const cancelled = payload => {
      const item = eventItem(payload)
      const id = getItemId(item) || payload?.id
      if (!id) return loadItems({ silent: true })
      setItems(current => current.filter(currentItem => getItemId(currentItem) !== id))
    }
    const cancelledOrder = payload => {
      if (!Array.isArray(payload?.ids)) return loadItems({ silent: true })
      const ids = new Set(payload.ids)
      setItems(current => current.filter(item => !ids.has(getItemId(item))))
    }

    socket.on('kds-item-created', created)
    socket.on('kds-item-updated', upsert)
    socket.on('kds-item-cancelled', cancelled)
    socket.on('kds-item-deleted', cancelled)
    socket.on('kds-items-deleted', cancelledOrder)
    socket.on('kds-order-activated', loadItems)
    socket.on('kds-order-cancelled', cancelledOrder)
    return () => {
      socket.off('kds-item-created', created)
      socket.off('kds-item-updated', upsert)
      socket.off('kds-item-cancelled', cancelled)
      socket.off('kds-item-deleted', cancelled)
      socket.off('kds-items-deleted', cancelledOrder)
      socket.off('kds-order-activated', loadItems)
      socket.off('kds-order-cancelled', cancelledOrder)
    }
  }, [configurations?.sound, loadItems, socket])

  const moveItem = async (item, targetIndex) => {
    const id = getItemId(item)
    const target = KDS_COLUMNS[targetIndex]
    if (!id || !target) return
    setUpdatingIds(current => new Set(current).add(id))
    try {
      const response = await api.patch(`/kds/items/${id}/status`, {
        status: statusValueFor(item, target.key),
        expectedStatus: getKdsStatus(item),
      })
      const updated = eventItem(response.data)
      if (getItemId(updated)) {
        setItems(current => current.map(currentItem => getItemId(currentItem) === id ? { ...currentItem, ...updated } : currentItem))
      } else {
        await loadItems({ silent: true })
      }
    } catch (updateError) {
      if (updateError?.response?.status === 409) {
        Toast.warning('Este item foi alterado em outra tela. A produção foi atualizada.')
        await loadItems({ silent: true })
      } else {
        Toast.warning(updateError?.response?.data?.message || 'Não foi possível alterar a etapa do item.')
      }
    } finally {
      setUpdatingIds(current => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen()
    else await document.querySelector('.kds-page')?.requestFullscreen()
  }

  if (!canView) {
    return <NavBar><div className="kds-denied"><FiAlertTriangle /><h1>Acesso não permitido</h1><p>Solicite a permissão de visualização do KDS.</p></div></NavBar>
  }

  const visibleItems = sectorFilter === 'none'
    ? items.filter(item => !getSector(item).id)
    : sectorFilter === 'all' ? items : items.filter(item => String(getSector(item).id) === String(sectorFilter))

  return (
    <NavBar>
      <main className="kds-page">
        <header className="kds-header">
          <div className="kds-title"><span><FiPackage /></span><div><h1>KDS</h1><p>Produção em tempo real</p></div></div>
          <div className="kds-header-tools">
            <span className={`kds-connection ${connected ? 'online' : 'offline'}`}>{connected ? <FiWifi /> : <FiWifiOff />} {connected ? 'Tempo real' : 'Reconectando'}</span>
            <time>{now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</time>
            <button type="button" onClick={() => loadItems({ silent: true })} disabled={refreshing} aria-label="Atualizar KDS"><FiRefreshCw className={refreshing ? 'spin' : ''} /></button>
            <button type="button" onClick={toggleFullscreen} aria-label={fullscreen ? 'Sair da tela cheia' : 'Abrir em tela cheia'}>{fullscreen ? <FiMinimize /> : <FiMaximize />}</button>
          </div>
        </header>

        <nav className="kds-filters" aria-label="Filtrar setor">
          <button className={sectorFilter === 'all' ? 'active' : ''} onClick={() => setSectorFilter('all')}>Geral</button>
          {sectors.map(sector => <button key={sector.id} className={String(sectorFilter) === String(sector.id) ? 'active' : ''} onClick={() => setSectorFilter(sector.id)}>{sector.name}</button>)}
          <button className={sectorFilter === 'none' ? 'active' : ''} onClick={() => setSectorFilter('none')}>Sem setor</button>
        </nav>

        {loading && <div className="kds-state"><span className="spinner-border" /><strong>Carregando produção...</strong></div>}
        {!loading && error && <div className="kds-state error"><FiAlertTriangle /><strong>Falha ao carregar o KDS</strong><p>{error}</p><button onClick={() => loadItems()}>Tentar novamente</button></div>}

        {!loading && !error && (
          <section className="kds-board">
            {KDS_COLUMNS.map(column => {
               const columnItems = visibleItems.filter(item => canonicalStatus(getKdsStatus(item)) === column.key)
              const groups = groupByOrder(columnItems)
              return (
                <section className={`kds-column status-${column.key}`} key={column.key}>
                  <header><div><i /><h2>{column.label}</h2></div><span>{columnItems.length}</span></header>
                  <div className="kds-column-content">
                     {groups.map(group => <OrderGroup key={group.key} group={group} showSector={sectorFilter === 'all'} canUpdate={canUpdate} updatingIds={updatingIds} onMove={moveItem} />)}
                    {groups.length === 0 && <div className="kds-empty"><FiPackage /><span>Nenhum item</span></div>}
                  </div>
                </section>
              )
            })}
          </section>
        )}
      </main>
    </NavBar>
  )
}
